<template>
  <div v-if="post" class="post">

    <!-- Post Header and Details (hidden for video posts — shown as overlay instead) -->
    <div class="post-header" v-if="!post.video">
      <a :href="`/${postAuthor.username}`">
        <img :src="postAuthor.avatar" alt="Author Profile" class="profile-pic" />
      </a>
      <div class="post-details">
        <p class="author">
          {{ postAuthor.username }}
          <img
            v-if="postAuthor.verified"
            src="@/assets/verified.png"
            alt="Verified"
            class="verified-badge"
            title="Verified Pro"
          />
          <router-link
            v-if="postAuthor.ski_schools && postAuthor.ski_schools.length > 0"
            :to="`/ski-school/${postAuthor.ski_schools[0].slug}`"
            class="ski-school-link"
          >
            <img
              :src="postAuthor.ski_schools[0].logo"
              alt="Ski School"
              class="ski-school-badge"
              :title="`${postAuthor.ski_schools[0].name} - ${postAuthor.ski_schools[0].role}`"
            />
          </router-link>
          <span v-if="postAuthor.verified" class="pro-badge"></span>
        </p>
        <p class="full-name">{{ postAuthor.first_name }} {{ postAuthor.last_name }}</p>
      </div>
      <div class="post-info">
        <span v-if="showCategoryBadge" class="info-badge category-badge">{{ categoryLabel }}</span>
        <span v-if="post.rope" class="info-badge">{{ post.rope }} meter</span>
        <span v-if="post.speed" class="info-badge">{{ post.speed }} kmph</span>
        <span v-if="trickPassLabel" class="info-badge">{{ trickPassLabel }}</span>
        <span v-if="post.jump_speed" class="info-badge">{{ post.jump_speed }} kmph</span>
        <span v-if="post.ramp_height" class="info-badge">{{ post.ramp_height }} m</span>
        <div class="menu-container">
          <span class="three-dots" @click="toggleDropdown(post.id)">&#x22EE;</span>
          <div class="dropdown-menu" v-if="isDropdownOpen(post.id)">
            <ul>
              <li v-if="isOwnPost" @click="showSkiSettings(post.id)">Ski settings</li>
              <li v-if="isOwnPost" @click="handleDropdownProTip(post.id)">Request Pro Tip</li>
              <li @click="sharePost">Share</li>
              <li @click="reportContent(post.id)">Report content</li>
              <li @click="blockUser(post.author?.id)">Block user</li>
              <li v-if="isOwnPost" @click="deletePost(post.id)">Delete post</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Post Content -->
    <div class="post-content">
      <img v-if="post.image" :src="post.image" alt="Post image" class="post-image" />
      <div 
        v-if="post.video"
        class="video-container"
        @click="handleVideoClick"
      >
        <video
          ref="video"
          :src="videoSrc"
          :poster="post.image || undefined"
          preload="none"
          muted
          autoplay
          loop
          playsinline
          class="post-video"
          @loadedmetadata="onVideoMetadataLoaded"
        ></video>

        <!-- Overlaid header for video posts (author, stats, menu) -->
        <div class="video-overlay-top">
          <div class="overlay-author" @click.stop>
            <a :href="`/${postAuthor.username}`">
              <img :src="postAuthor.avatar" alt="Author Profile" class="overlay-avatar" />
            </a>
            <div class="overlay-details">
              <p class="overlay-username">
                {{ postAuthor.username }}
                <img
                  v-if="postAuthor.verified"
                  src="@/assets/verified.png"
                  alt="Verified"
                  class="verified-badge"
                  title="Verified Pro"
                />
                <router-link
                  v-if="postAuthor.ski_schools && postAuthor.ski_schools.length > 0"
                  :to="`/ski-school/${postAuthor.ski_schools[0].slug}`"
                  class="ski-school-link"
                >
                  <img
                    :src="postAuthor.ski_schools[0].logo"
                    alt="Ski School"
                    class="ski-school-badge"
                    :title="`${postAuthor.ski_schools[0].name} - ${postAuthor.ski_schools[0].role}`"
                  />
                </router-link>
              </p>
              <p class="overlay-fullname">{{ postAuthor.first_name }} {{ postAuthor.last_name }}</p>
            </div>
          </div>

          <div class="overlay-right" @click.stop>
            <span v-if="showCategoryBadge" class="overlay-badge category-badge">{{ categoryLabel }}</span>
            <span v-if="post.rope" class="overlay-badge">{{ post.rope }} m</span>
            <span v-if="post.speed" class="overlay-badge">{{ post.speed }} kmph</span>
            <span v-if="trickPassLabel" class="overlay-badge">{{ trickPassLabel }}</span>
            <span v-if="post.jump_speed" class="overlay-badge">{{ post.jump_speed }} kmph</span>
            <span v-if="post.ramp_height" class="overlay-badge">{{ post.ramp_height }} m</span>
            <div class="menu-container">
              <span class="three-dots overlay-dots" @click="toggleDropdown(post.id)">&#x22EE;</span>
              <div class="dropdown-menu" v-if="isDropdownOpen(post.id)">
                <ul>
                  <li v-if="isOwnPost" @click="showSkiSettings(post.id)">Ski settings</li>
                  <li v-if="isOwnPost" @click="handleDropdownProTip(post.id)">Request Pro Tip</li>
                  <li @click="sharePost">Share</li>
                  <li @click="reportContent(post.id)">Report content</li>
                  <li @click="blockUser(post.author?.id)">Block user</li>
                  <li v-if="isOwnPost" @click="deletePost(post.id)">Delete post</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div v-if="isVideoPaused" class="play-button-overlay">
          <i class="fas fa-play"></i>
        </div>
       <!-- <div class="fullscreen-button" 
             v-if="shouldShowFullscreenButton"
             @click.stop="toggleFullscreen">
          <i :class="isFullscreen ? 'fas fa-compress' : 'fas fa-expand'"></i>
        </div>     Toggle breaks the app for now, so we're not using it.-->
      </div>
      <p class="post-text">
        {{ postContent }}
        <span class="post-date-inline clickable-date" @click="navigateToPost"> · {{ formatDate(postCreated) }}</span>
      </p>
    </div>
    
    <!-- Request Pro Tip Button (only for own posts) -->
    <div v-if="isOwnPost" class="request-pro-tip-container">
      <router-link 
        v-if="!isMobile"
        :to="`/request-pro-tip?post=${post.id}`" 
        class="request-pro-tip-button"
      >
        <span>Request Pro Tip</span>
      </router-link>
      <button 
        v-else
        @click="openProTipInBrowser" 
        class="request-pro-tip-button mobile-pro-tip"
      >
        <span>Request Pro Tip</span>
      </button>
    </div>
    
    <div id="loading" style="display: none;">Loading...</div>
    <!-- Post Actions -->
    <div class="post-actions">
      <div class="tips-section" @click="toggleTips">
        <i class="fas fa-comments"></i>
        <span v-if="localTipsCount !== undefined">{{ localTipsCount }} Waterski Tips</span>
      </div>
      
      <div class="action-group">
        <div class="views-section">
          <i class="far fa-eye"></i>
          <span>{{ post.view_count || 0 }}</span>
        </div>
        <div class="share-section" @click="sharePost">
          <i class="fas fa-share-alt"></i>
        </div>
        <div class="likes-section" @click="handleLike">
          <i :class="localLikedByUser ? 'fas fa-heart liked' : 'far fa-heart not-liked'"></i>
          <span v-if="localLikesCount !== undefined">{{ localLikesCount }}</span>
        </div>
      </div>
    </div>
    
    <!-- Waterski Tips -->
    <WaterskiTips
      v-if="showTips && post.id"
      :post-creator-id="post.author.user_id"
      :post-id="post.id"
      :initial-tips="preloadedTips"
      :show-new-tip-form="true"
      @update:tips-count="updateTipsCount"
    />

    <!-- Ski Settings Modal -->
    <div v-if="showSkiSettingsModal" class="ski-settings-modal" @click="closeSkiSettings">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Ski Settings for this video</h3>
          <button @click="closeSkiSettings" class="close-button">&times;</button>
        </div>
        <div class="modal-body">
          <div v-if="currentSkiSettings" class="ski-settings-grid">
            <div v-if="currentSkiSettings.boot_placement" class="setting-item">
              <label>Boot Placement:</label>
              <span>{{ currentSkiSettings.boot_placement }}</span>
            </div>
            <div v-if="currentSkiSettings.fin_length" class="setting-item">
              <label>Fin Length:</label>
              <span>{{ currentSkiSettings.fin_length }}</span>
            </div>
            <div v-if="currentSkiSettings.fin_depth" class="setting-item">
              <label>Fin Depth:</label>
              <span>{{ currentSkiSettings.fin_depth }}</span>
            </div>
            <div v-if="currentSkiSettings.fin_dft" class="setting-item">
              <label>Fin DFT:</label>
              <span>{{ currentSkiSettings.fin_dft }}</span>
            </div>
            <div v-if="currentSkiSettings.wing_degree" class="setting-item">
              <label>Wing Degree:</label>
              <span>{{ currentSkiSettings.wing_degree }}</span>
            </div>
          </div>
          <div v-else class="no-settings">
            <p>No ski settings added yet.</p>
            <router-link to="/ski-settings" class="update-settings-link" @click="closeSkiSettings">
              Update your ski settings
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL, WEB_BASE_URL } from '@/config';
import axios from 'axios';
import WaterskiTips from '@/components/WaterskiTips.vue';
import { ref } from 'vue';
import { Capacitor } from '@capacitor/core';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { Share } from '@capacitor/share';

export default {
  name: 'PostCard',
  components: {
    WaterskiTips
  },
  props: {
    post: {
      type: Object,
      required: true
    },
    publicMode: {
      type: Boolean,
      default: false
    },
    // Optional shared user context provided by the parent page so each card
    // doesn't independently fetch /api/profile/ and /api/user-comment-rates/.
    sharedUserId: {
      type: [Number, String],
      default: null
    },
    sharedUserVotes: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loadingTips: {},
      newTip: {},
      newReply: {},
      userVotes: {},
      dropdownStates: {},
      userID: null,
      videoStates: ref({}),
      lastTap: 0,
      pinchStartDistance: null,
      showTips: false,
      tips: [],
      videoSrc: null, // Lazily attached when the video nears the viewport
      videoObserver: null,
      localLikedByUser: false,
      localLikesCount: 0,
      localLikeId: null,
      localTipsCount: 0,
      preloadedTips: [],
      pausedVideos: new Set(),
      isVideoPaused: false,
      isFullscreen: false,
      isNative: Capacitor.isNativePlatform(),
      showSkiSettingsModal: false,
      currentSkiSettings: null,
      isMobile: false,
      shouldShowFullscreenButton: false,
    };
  },
  computed: {
    // Add computed properties to safely access post data
    postAuthor() {
      return this.post?.author || {
        username: '',
        avatar: 'default-avatar.png',
        first_name: 'Unknown',
        last_name: ''
      };
    },
    // Human-readable discipline label; only shown for non-slalom posts so
    // existing slalom videos stay uncluttered (they already show rope/speed).
    categoryLabel() {
      const labels = { slalom: 'Slalom', trick: 'Trick', jump: 'Jump' };
      return labels[this.post?.category] || '';
    },
    // Human-readable trick pass label (hands/toes -> Hands/Toes).
    trickPassLabel() {
      const labels = { hands: 'Hands', toes: 'Toes' };
      return labels[this.post?.trick_pass] || '';
    },
    showCategoryBadge() {
      return this.post?.category && this.post.category !== 'slalom';
    },
    postContent() {
      return this.post?.content || '';
    },
    postCreated() {
      return this.post?.created || new Date();
    },
    postTips() {
      return this.tips;
    },
    postTipsCount() {
      return this.post?.tips_count || 0;
    },
    postLikesCount() {
      return this.post?.likes_count || 0;
    },
    postLikedByUser() {
      return this.post?.liked_by_user || false;
    },
    isOwnPost() {
      return this.userID === this.post?.author?.id;
    },
  },
  metaInfo: {
    title: 'Post Card',
    meta: [
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no' }
    ]
  },
  mounted() {
    this.initUserContext();
    this.preloadTips();
    this.setupVideoObserver();
    this.checkIfMobile();
    document.addEventListener('click', this.handleClickOutside);
  },
  watch: {
    post: {
      immediate: true,
      handler(newPost) {
        if (newPost) {
          this.localLikedByUser = newPost.liked_by_user;
          this.localLikesCount = newPost.likes_count;
          this.localLikeId = newPost.like_id;
          this.localTipsCount = newPost.tips_count;
        }
      }
    },
    sharedUserId(newId) {
      if (newId !== null && newId !== undefined) {
        this.userID = newId;
      }
    },
    sharedUserVotes(newVotes) {
      if (newVotes && Object.keys(newVotes).length > 0) {
        this.userVotes = { ...newVotes };
      }
    }
  },
  methods: {
    handleVideoClick() {
      console.log('Video clicked');
      const videoElement = this.$refs.video;
      console.log('Video element:', videoElement);
      console.log('Is paused:', videoElement?.paused);
      
      if (videoElement) {
        if (videoElement.paused) {
          console.log('Attempting to play video');
          videoElement.play()
            .then(() => {
              console.log('Video started playing');
              this.isVideoPaused = false;
            })
            .catch(error => console.error('Error playing video:', error));
        } else {
          console.log('Attempting to pause video');
          videoElement.pause();
          this.isVideoPaused = true;
          console.log('Video paused');
        }
      }
    },
    async fetchComments(postId) {
      this.loadingTips[postId] = true;
      try {
        const response = await axios.get(`${API_BASE_URL}/api/post-comments/${postId}/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
        });

        this.tips = response.data.map(tip => ({
          ...tip,
          userVote: this.userVotes[tip.id]?.rate || 0,
          replies: [],
          reply_count: tip.reply_count || 0,
          showReplies: false,
        }));
      } catch (error) {
        console.error('Error fetching tips:', error);
      } finally {
        this.loadingTips[postId] = false;
      }
    },
    async createTip(postId) {
      if (!this.newTip[postId] || !this.newTip[postId].trim()) return;

      try {
        const token = localStorage.getItem('access_token');
        await axios.post(
          `${API_BASE_URL}/api/comment-create/`,
          { post: postId, body: this.newTip[postId] },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        this.newTip[postId] = '';
        this.fetchComments(postId);
      } catch (error) {
        console.error('Error creating tip:', error);
      }
    },
    async handleLike() {
      if (this.publicMode) {
        this.$emit('click:like');
        return;
      }
      const token = localStorage.getItem('access_token');

      // Optimistically update the UI using local data
      this.localLikedByUser = !this.localLikedByUser;
      this.localLikesCount += this.localLikedByUser ? 1 : -1;

      try {
        if (!this.localLikedByUser) {
          await axios.delete(`${API_BASE_URL}/api/like-delete/${this.localLikeId}/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          this.localLikeId = null;
        } else {
          const response = await axios.post(
            `${API_BASE_URL}/api/like-create/`,
            { post: this.post.id, user: this.userID, value: 'like' },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          this.localLikeId = response.data.id;
        }
        
        // Instead of modifying the post prop directly, emit an event
        this.$emit('like-updated', {
          liked_by_user: this.localLikedByUser,
          likes_count: this.localLikesCount,
          like_id: this.localLikeId
        });
      } catch (error) {
        // Revert the optimistic update if there's an error
        this.localLikedByUser = !this.localLikedByUser;
        this.localLikesCount += this.localLikedByUser ? 1 : -1;
        console.error('Error handling like:', error.response ? error.response.data : error.message);
      }
    },
    async handleVote(tip, value) {
      if (this.publicMode) {
        this.$emit('click:rate');
        return;
      }
      const currentVote = tip.userVote || 0;

      try {
        const token = localStorage.getItem('access_token');
        const rateId = this.userVotes[tip.id]?.rateId;

        if (currentVote !== 0 && currentVote !== value) {
          await axios.delete(`${API_BASE_URL}/api/rate-delete/${rateId}/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          this.updateTipVotes(tip, currentVote, 0);
        }

        if (currentVote !== value) {
          const requestData = { comment: tip.id, rate: value, user: this.userID };
          let response;

          if (rateId && currentVote !== 0) {
            response = await axios.post(
              `${API_BASE_URL}/api/rate-update/${rateId}/`,
              requestData,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
          } else {
            response = await axios.post(
              `${API_BASE_URL}/api/rate-create/`,
              requestData,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
          }

          this.userVotes[tip.id] = {
            rateId: response.data.id,
            rate: value,
          };
          this.updateTipVotes(tip, 0, value);
        } else if (currentVote === value) {
          await axios.delete(`${API_BASE_URL}/api/rate-delete/${rateId}/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          this.updateTipVotes(tip, currentVote, 0);
        }
      } catch (error) {
        console.error('Error handling vote:', error.response ? error.response.data : error.message);
      }
    },
    updateTipVotes(tip, currentVote, newVote) {
      if (newVote === 0) {
        tip.ratecount -= currentVote;
        tip.userVote = 0;
        delete this.userVotes[tip.id];
      } else {
        tip.ratecount += newVote - currentVote;
        tip.userVote = newVote;
        this.userVotes[tip.id] = { ...this.userVotes[tip.id], rate: newVote };
      }
    },
    getUserVote(tipId) {
      return this.userVotes[tipId]?.rate || 0;
    },
    // Use the shared user context from the parent page when available;
    // otherwise fall back to fetching it ourselves (standalone usage).
    initUserContext() {
      if (this.sharedUserId !== null && this.sharedUserId !== undefined) {
        this.userID = this.sharedUserId;
        if (this.sharedUserVotes && Object.keys(this.sharedUserVotes).length > 0) {
          this.userVotes = { ...this.sharedUserVotes };
        } else {
          this.fetchUserVotes();
        }
      } else {
        this.getUserID();
      }
    },
    async getUserID() {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${API_BASE_URL}/api/profile/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        this.userID = response.data.id; // Ensure this line sets the correct user ID
        this.fetchUserVotes();
      } catch (error) {
        console.error('Error fetching user ID:', error);
      }
    },
    async fetchUserVotes() {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${API_BASE_URL}/api/user-comment-rates/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        this.userVotes = response.data.reduce((acc, vote) => {
          acc[vote.comment] = { rate: vote.rate, rateId: vote.id };
          return acc;
        }, {});
      } catch (error) {
        console.error('Error fetching user votes:', error);
      }
    },
    toggleTips() {
      if (this.publicMode) {
        this.$emit('click:tips');
        return;
      }
      this.showTips = !this.showTips;
    },
    formatDate(dateString) {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },
    async confirmDelete(commentId) {
      if (confirm('Are you sure you want to delete this comment?')) {
        try {
          const token = localStorage.getItem('access_token');
          await axios.delete(`${API_BASE_URL}/api/comment-delete/${commentId}/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          this.tips = this.tips.filter(tip => tip.id !== commentId);
          this.$emit('update:tips-count', (this.post.tips_count || 0) - 1);
        } catch (error) {
          console.error('Error deleting comment:', error);
        }
      }
    },
    async confirmReplyDelete(replyId) {
      if (confirm('Are you sure you want to delete this reply?')) {
        try {
          const token = localStorage.getItem('access_token');
          await axios.delete(`${API_BASE_URL}/api/reply-delete/${replyId}/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          this.removeReplyFromTip(replyId);
          
        } catch (error) {
          console.error('Error deleting reply:', error);
        }
      }
    },
    removeReplyFromTip(replyId) {
      this.tips.forEach(tip => {
        if (tip.replies) {
          tip.replies = tip.replies.filter(reply => reply.id !== replyId);
          // Update reply count
          tip.reply_count = tip.replies.length;
        }
      });
    },
    async fetchReplyCount(tipId) {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/comment-reply-count/${tipId}/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
        });
        return response.data.count;
      } catch (error) {
        console.error('Error fetching reply count:', error);
        return 0;
      }
    },
    async createReply(tipId) {
      if (this.publicMode) {
        this.$emit('click:reply');
        return;
      }
      if (!this.newReply[tipId] || !this.newReply[tipId].trim()) return;

      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.post(
          `${API_BASE_URL}/api/reply-create/`,
          {
            comment: tipId,
            body: this.newReply[tipId],
            user: this.userID,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.id) {
          this.newReply[tipId] = ''; // Clear the input field

          // Find the tip and update its replies
          const tip = this.tips.find(t => t.id === tipId);
          if (tip) {
            if (!tip.replies) tip.replies = [];
            tip.replies.push(response.data);
            tip.reply_count = (tip.reply_count || 0) + 1;
          }
        }
      } catch (error) {
        console.error('Error creating reply:', error);
      }
    },
    async toggleReplies(tipId) {
      // Find the tip in our local tips array
      const tip = this.tips.find(t => t.id === tipId);
      if (!tip) return;

      tip.showReplies = !tip.showReplies;

      if (tip.showReplies && (!tip.replies || tip.replies.length === 0)) {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/api/comment-replies/${tipId}/`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('access_token')}`,
              },
            }
          );
          tip.replies = response.data;
        } catch (error) {
          console.error('Error fetching replies:', error);
          tip.replies = [];
        }
      }
    },
    toggleDropdown(postId, event) {
      if (this.publicMode) {
        this.$emit('click:menu');
        return;
      }
      
      if (event) {
        event.stopPropagation();
      }
      this.dropdownStates[postId] = !this.dropdownStates[postId];
    },
    isDropdownOpen(postId) {
      return this.dropdownStates[postId] || false;
    },
    async reportContent(postId) {
      if (this.publicMode) {
        this.$emit('click:menu');
        return;
      }
      
      this.dropdownStates[postId] = false;

      try {
        const token = localStorage.getItem('access_token');
        const userId = this.userID; 

        if (!token) {
          alert('You need to be logged in to report content.');
          return;
        }

        if (!userId) {
          console.error('User ID not found!');
          return;
        }

        const response = await axios.post(
          `${API_BASE_URL}/api/report-post/`, 
          { 
            post: postId,  
            user: userId   
          },  
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        console.log('API Response:', response.data);

        alert(`Reported content for post ID: ${postId}. Thank you for your feedback!`);
      } catch (error) {
        console.error('Error reporting post:', error.response ? error.response.data : error.message);
        alert('There was an error reporting the post. Please try again later.');
      }
    },
    async blockUser(postId) {
      if (this.publicMode) {
        this.$emit('click:menu');
        return;
      }
      
      this.dropdownStates[postId] = false;

      try {
        const token = localStorage.getItem('access_token');
        const userId = this.userID; 

        if (!token) {
          alert('You need to be logged in to block a user.');
          return;
        }

        if (!userId) {
          console.error('User ID not found!');
          return;
        }

        const response = await axios.post(
          `${API_BASE_URL}/api/report-post/`, 
          { 
            post: postId,  
            user: userId   
          },  
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        console.log('API Response:', response.data);

        alert(`You succesfully blocked this user. Thank you for your feedback!`);
      } catch (error) {
        console.error('Error blocking this user:', error.response ? error.response.data : error.message);
        alert('There was an error when blocking this user. Please try again later.');
      }
    },
    async preloadTips() {
      try {
        // First, ensure we have the user votes
        if (Object.keys(this.userVotes).length === 0) {
          await this.fetchUserVotes();
        }

        const response = await axios.get(`${API_BASE_URL}/api/post-comments/${this.post.id}/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
        });

        this.preloadedTips = response.data.map(tip => ({
          ...tip,
          userVote: this.userVotes[tip.id]?.rate || 0,  // This sets the user's vote
          replies: [],
          reply_count: tip.reply_count || 0,
          showReplies: false,
        }));
      } catch (error) {
        console.error('Error preloading tips:', error);
      }
    },
    updateTipsCount() {
      if (!this.post || !this.post.id) return;
      
      axios.get(`${API_BASE_URL}/api/post/${this.post.id}/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      }).then(response => {
        this.localTipsCount = response.data.tips_count;
        this.$emit('tips-count-updated', response.data.tips_count);
      }).catch(error => {
        console.error('Error updating tips count:', error);
      });
    },
    async toggleFullscreen(event) {
      event.stopPropagation(); // Prevent video play/pause
      
      if (this.isNative) {
        try {
          if (!this.isFullscreen) {
            // Enter fullscreen (landscape)
            await ScreenOrientation.lock({
              orientation: 'landscape'
            });
            
            // Add fullscreen class to video container
            event.target.closest('.video-container').classList.add('mobile-fullscreen');
            this.isFullscreen = true;
          } else {
            // Exit fullscreen (back to portrait)
            await ScreenOrientation.unlock();
            await ScreenOrientation.lock({
              orientation: 'portrait'
            });
            
            // Remove fullscreen class
            event.target.closest('.video-container').classList.remove('mobile-fullscreen');
            this.isFullscreen = false;
          }
        } catch (error) {
          console.error('Error toggling fullscreen:', error);
        }
      } else {
        // Use existing web fullscreen code
        const videoContainer = event.target.closest('.video-container');
        
        if (!this.isFullscreen) {
          try {
            if (videoContainer.requestFullscreen) {
              await videoContainer.requestFullscreen();
            } else if (videoContainer.webkitRequestFullscreen) {
              await videoContainer.webkitRequestFullscreen();
            }
            this.isFullscreen = true;
          } catch (error) {
            console.error('Error entering fullscreen:', error);
          }
        } else {
          try {
            if (document.exitFullscreen) {
              await document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
              await document.webkitExitFullscreen();
            }
            this.isFullscreen = false;
          } catch (error) {
            console.error('Error exiting fullscreen:', error);
          }
        }
      }
    },
    async deletePost(postId) {
      if (confirm('Are you sure you want to delete this post?')) {
        try {
          const token = localStorage.getItem('access_token');
          await axios.delete(`${API_BASE_URL}/api/post-delete/${postId}/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          
          // Hide the post immediately by setting post to null
          this.$emit('update:post', null);
          
          // Also emit post-deleted event for any parent components that need it
          this.$emit('post-deleted', postId);
          
          // Close the dropdown
          this.dropdownStates[postId] = false;
        } catch (error) {
          console.error('Error deleting post:', error);
          alert('There was an error deleting the post. Please try again later.');
        }
      }
    },
    handleClickOutside(event) {
      const dropdowns = document.querySelectorAll('.dropdown-menu');
      const threeDots = document.querySelectorAll('.three-dots');
      
      let clickedOutside = true;
      
      [...dropdowns, ...threeDots].forEach(element => {
        if (element.contains(event.target)) {
          clickedOutside = false;
        }
      });

      if (clickedOutside) {
        this.dropdownStates = {};
      }
    },
    async sharePost() {
      try {
        if (this.publicMode) {
          this.$emit('click:share');
          return;
        }

        const postUrl = `${WEB_BASE_URL}/post/${this.post.id}`;
        
        if (Capacitor.isNativePlatform()) {
          // Use native sharing on mobile
          await Share.share({
            title: 'Check out this water ski video 6buoys',
            text: `${this.postAuthor.username} shared: ${this.postContent.substring(0, 100)}${this.postContent.length > 100 ? '...' : ''}`,
            url: postUrl,
            dialogTitle: 'Share this post'
          });
        } else {
          // Use Web Share API if available, otherwise copy to clipboard
          if (navigator.share) {
            await navigator.share({
              title: 'Check out this water ski video on 6buoys',
              text: `${this.postAuthor.username} shared: ${this.postContent.substring(0, 100)}${this.postContent.length > 100 ? '...' : ''}`,
              url: postUrl
            });
          } else {
            // Fallback to copying to clipboard with better user feedback
            await navigator.clipboard.writeText(postUrl);
            
            // Show success alert
            alert('Post link copied to clipboard!\n\n' + postUrl);
            
            // Also show temporary visual feedback
            const shareElement = event.target.closest('.share-section');
            const originalHTML = shareElement.innerHTML;
            shareElement.innerHTML = '<i class="fas fa-check" style="color: #1da1f2;"></i>';
            
            setTimeout(() => {
              shareElement.innerHTML = originalHTML;
            }, 2000);
          }
        }
      } catch (error) {
        console.error('Error sharing post:', error);
        
        // Fallback: try to copy to clipboard
        try {
          const postUrl = `${WEB_BASE_URL}/post/${this.post.id}`;
          await navigator.clipboard.writeText(postUrl);
          alert('Post link copied to clipboard!\n\n' + postUrl);
        } catch (clipboardError) {
          console.error('Error copying to clipboard:', clipboardError);
          // Final fallback - show the URL for manual copying
          prompt('Copy this link:', `${WEB_BASE_URL}/post/${this.post.id}`);
        }
      }
    },
    setupVideoObserver() {
      if (!this.$refs.video) return;
      
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const video = entry.target;
          if (entry.isIntersecting) {
            // Lazily attach the source only when the video nears the viewport
            // so off-screen videos don't all buffer at once.
            if (!this.videoSrc && this.post && this.post.video) {
              this.videoSrc = this.post.video;
            }
            // Video is visible, try to autoplay
            video.play().catch(error => {
              console.log('Autoplay prevented:', error);
            });
          } else {
            // Video not visible, pause it
            video.pause();
          }
        });
      }, {
        threshold: 0.5, // Play when 50% visible
        rootMargin: '300px 0px' // Start loading slightly before it scrolls into view
      });
      
      observer.observe(this.$refs.video);
      this.videoObserver = observer;
    },
    showSkiSettings(postId) {
      this.dropdownStates[postId] = false; // Close dropdown
      
      if (this.post.ski_settings_snapshot) {
        this.currentSkiSettings = this.post.ski_settings_snapshot;
      } else {
        this.currentSkiSettings = null;
      }
      
      this.showSkiSettingsModal = true;
    },
    
    closeSkiSettings() {
      this.showSkiSettingsModal = false;
      this.currentSkiSettings = null;
    },
    navigateToPost() {
      if (this.publicMode) {
        this.$emit('click:date');
        return;
      }
      this.$router.push(`/post/${this.post.id}`);
    },
    checkIfMobile() {
      this.isMobile = window.innerWidth <= 600;
    },
    openProTipInBrowser() {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      
      // Generate a temporary state parameter for security
      const state = Math.random().toString(36).substring(2, 15);
      
      // Store the state in localStorage to verify later
      localStorage.setItem('auth_state', state);
      
      // Open the URL with the token and post ID as query parameters
      const url = `${WEB_BASE_URL}/request-pro-tip?token=${encodeURIComponent(token)}&state=${state}&post=${this.post.id}`;
      window.open(url, '_blank');
    },
    handleDropdownProTip(postId) {
      // Close the dropdown first
      this.dropdownStates[postId] = false;
      
      if (this.isMobile) {
        // On mobile, open in browser
        this.openProTipInBrowser();
      } else {
        // On web, navigate to the route
        this.$router.push(`/request-pro-tip?post=${this.post.id}`);
      }
    },
    onVideoMetadataLoaded() {
      const video = this.$refs.video;
      if (!video) return;
      
      const aspectRatio = video.videoWidth / video.videoHeight;
      
      // Show fullscreen button for landscape videos (width > height)
      // or videos with aspect ratio greater than 0.75 (not too vertical)
      // You can adjust these thresholds as needed:
      // - aspectRatio > 1.0 = only landscape videos
      // - aspectRatio > 0.75 = allow slightly vertical videos
      // - aspectRatio > 0.5 = allow more vertical videos
      const minAspectRatio = 0.75; // Adjust this value as needed
      
      this.shouldShowFullscreenButton = aspectRatio > minAspectRatio;
      
      console.log(`Video dimensions: ${video.videoWidth}x${video.videoHeight}, aspect ratio: ${aspectRatio.toFixed(2)}, showing fullscreen: ${this.shouldShowFullscreenButton}`);
    },
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
    if (this.videoObserver) {
      this.videoObserver.disconnect();
      this.videoObserver = null;
    }
    if (this.isNative && this.isFullscreen) {
      ScreenOrientation.unlock();
      ScreenOrientation.lock({
        orientation: 'portrait'
      });
    }
  },
};
</script>

<style scoped>
/* General Styles */
body {
  background-color: hsl(210, 34%, 13%);
  color: #d9d9d9;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}


.post {
  border-radius: 16px;
  margin-bottom: 20px;
  background-color: #192734;
  overflow: hidden;
  transition: background-color 0.3s ease;
}

.post:hover {
  background-color: #22303c;
}

.post-header {
  display: flex;
  align-items: center;
  text-align: left;
  padding: 12px 16px;
}

@media screen and (max-width: 600px) {
  .post-header {
    padding: 12px 10px;
  }
}

.post-header a {
  display: flex;
  align-items: center;
}

.profile-pic {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  margin-right: 12px;
  border: 2px solid #1da1f2;
  flex-shrink: 0;
}

.post-details {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0px;
}

.author {
  font-weight: bold;
  color: #ffffff;
  margin: 0;
  font-size: 1em;
  display: flex;
  align-items: center;
  gap: 5px;
}

.full-name {
  font-size: 0.85em;
  color: #8899a6;
  margin: -2px 0 0 0;
}

.post-date {
  font-size: 0.85em;
  color: #8899a6;
  text-align: center;
}

.post-date-inline {
  font-size: 0.85em;
  color: #8899a6;
}

.clickable-date {
  cursor: pointer;
  transition: color 0.2s ease;
}

.clickable-date:hover {
  color: #1da1f2;
  text-decoration: underline;
}

.post-info {
  display: flex;
  gap: 8px;
  align-items: center;
}

@media screen and (max-width: 600px) {
  .post-info {
    padding-right: 0;
  }
}

.verified-badge {
  width: 18px;
  height: 18px;
}

.verified-badge:hover {
  position: relative;
}

.verified-badge[title]:hover::after {
  content: attr(title);
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 1000;
  animation: tooltipFadeIn 0.1s ease-in;
}

@keyframes tooltipFadeIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.ski-school-badge {
  width: 18px;
  height: 18px;
  border-radius: 2px;
  object-fit: cover;
}

.ski-school-link {
  text-decoration: none;
  color: inherit;
}

.info-badge {
  display: inline-block;
  color: #ffffff;
  padding: 4px 10px;
  border: #38444d 1px solid;
  border-radius: 20px;
  font-size: 0.85em;
  font-weight: bold;
  line-height: 1.5;
  text-align: center;
}

.category-badge {
  background-color: #1da1f2;
  border-color: #1da1f2;
  color: #ffffff;
}

.post-content {
  padding: 0px 16px;
}

@media screen and (max-width: 600px) {
  .post-content {
    padding: 0px 10px;
  }
}

.post-image {
  width: 100%;
  border-radius: 16px;
  max-height: 400px;
  object-fit: cover;
  margin-bottom: 12px;
}

.post-video {
  width: 100%;
  display: block;
  border-radius: 0;
  margin-bottom: 12px;
}

.post-text {
  font-size: 1em;
  color: #ffffff;
  text-align: left;
  margin: 0;
}

.post-actions {
  padding: 10px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #8899a6;
  font-size: 1em;
}

@media screen and (max-width: 600px) {
  .post-actions {
    padding: 10px;
  }
}

.menu-container {
  position: relative;
  display: inline-block;
}

.three-dots {
  cursor: pointer;
  font-size: 18px;
  color: #8899a6; /* Match the color with the rest of the theme */
}

.dropdown-menu {
  display: block; /* Ensure the dropdown shows up */
  position: absolute;
  right: 0;
  background-color: #192734; /* Match the dark background of the app */
  border: 1px solid #38444d; /* Border style to match the app theme */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3); /* Slight shadow for depth */
  z-index: 10;
  width: 160px; /* Increase the width for more space */
  border-radius: 8px; /* Round the corners */
}

.dropdown-menu ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.dropdown-menu ul li {
  padding: 10px 15px; /* Adjust padding for better spacing */
  cursor: pointer;
  color: #e1e8ed; /* Light text color for readability */
}

.dropdown-menu ul li:hover {
  background-color: #22303c; /* Slightly lighter dark color on hover */
}

.tips-section {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  color: white;
}

/* Replace the hover-only style with a more mobile-friendly approach */
@media (hover: hover) {
  /* Only apply hover effects on devices that support hover */
  .tips-section:hover {
    color: #8899a6;
  }
}

.request-pro-tip-container {
  display: flex;
  justify-content: flex-end;
  padding: 8px 16px 0 16px;
}

@media screen and (max-width: 600px) {
  .request-pro-tip-container {
    padding: 8px 10px 0 10px;
  }
}

.request-pro-tip-button {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background-color: rgba(29, 161, 242, 0.1);
  border: 1px solid #1da1f2;
  border-radius: 12px;
  color: #1da1f2;
  text-decoration: none;
  font-size: 0.75em;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.request-pro-tip-button i {
  font-size: 0.8em;
}

.request-pro-tip-button:hover {
  background-color: rgba(29, 161, 242, 0.2);
  border-color: #0c8ddb;
  color: #0c8ddb;
  transform: scale(1.02);
}

.request-pro-tip-button.mobile-pro-tip {
  background-color: rgba(29, 161, 242, 0.1);
  font-family: inherit;
}

.action-group {
  display: flex;
  align-items: center;
  gap: 15px; /* This creates the slight gap between views and likes */
}

.views-section {
  display: flex;
  align-items: center;
  gap: 5px;
  color: white;
}

.views-section i {
  color: white;
}

.likes-section {
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  color: white;
}

.likes-section i {
  transition: color 0.2s;
}

.likes-section i.liked {
  color: #e0245e;
}

.likes-section i.not-liked {
  color: white;
  border: none;
}

.likes-section i:hover {
  color: #ff5a78;
  transform: scale(1.2);
}

.video-container {
  position: relative;
  /* Full-bleed: cancel the .post-content side padding so the video spans the card edge-to-edge */
  width: auto;
  margin: 0 -16px 0 -16px;
  cursor: pointer;
}

@media screen and (max-width: 600px) {
  .video-container {
    margin: 0 -10px 0 -10px;
  }
}

.post-video {
  width: 100%;
  display: block;
  border-radius: 0;
  margin-bottom: 12px;
}

/* Overlaid header on video posts */
.video-overlay-top {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 16px;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.25) 60%, rgba(0, 0, 0, 0) 100%);
  pointer-events: none;
  z-index: 3;
}

/* Video has a 12px bottom margin from the container; overlay aligns to the video top */
.overlay-author,
.overlay-right {
  pointer-events: auto;
}

.overlay-author {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.overlay-author a {
  display: flex;
  align-items: center;
}

.overlay-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid #1da1f2;
  flex-shrink: 0;
}

.overlay-details {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
}

.overlay-username {
  margin: 0;
  font-weight: bold;
  color: #ffffff;
  font-size: 0.95em;
  display: flex;
  align-items: center;
  gap: 5px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
  white-space: nowrap;
}

.overlay-fullname {
  margin: -3px 0 0 0;
  padding-left: 0;
  text-align: left;
  font-size: 0.8em;
  color: #e1e8ed;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
}

.overlay-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.overlay-badge {
  display: inline-block;
  color: #ffffff;
  padding: 3px 8px;
  background-color: rgba(0, 0, 0, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 20px;
  font-size: 0.75em;
  font-weight: bold;
  line-height: 1.4;
  white-space: nowrap;
  backdrop-filter: blur(2px);
}

.overlay-dots {
  color: #ffffff;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
  padding: 0 4px;
}

.play-button-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 2rem;
  color: rgba(255, 255, 255, 0.56);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
}

.play-button-overlay i {
  pointer-events: none;
}

.loading-tips {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.fullscreen-button {
  position: absolute;
  top: 10px;
  right: 10px;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 2;
}

.fullscreen-button:hover {
  background-color: rgba(0, 0, 0, 0.7);
}

.video-container:fullscreen .post-video {
  width: 100vw;
  height: 100vh;
  margin: 0;
  object-fit: contain;
  background: black;
}

.video-container:fullscreen {
  background: black;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Add these new styles for mobile fullscreen */
.video-container.mobile-fullscreen {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  z-index: 9999 !important;
  background: black !important;
  margin: 0 !important;
  padding: 0 !important;
}

.video-container.mobile-fullscreen .post-video {
  width: 100% !important;
  height: 100% !important;
  object-fit: contain !important;
  margin: 0 !important;
  border-radius: 0 !important;
}

.video-container.mobile-fullscreen .fullscreen-button {
  top: 20px;
  right: 20px;
}

.username {
  font-size: 0.85em;
  color: #8899a6;
  margin: 0;
}

.share-section {
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  color: white;
}

.share-section i {
  transition: transform 0.2s;
}

.share-section i:hover {
  transform: scale(1.2);
}

#loading {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 15px 30px;
  border-radius: 8px;
  z-index: 9999;
}

/* Ski Settings Modal */
.ski-settings-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: #192734;
  border-radius: 12px;
  max-width: 90%;
  width: 400px;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #38444d;
}

.modal-header h3 {
  margin: 0;
  color: #1da1f2;
  font-size: 1.2em;
}

.close-button {
  background: none;
  border: none;
  color: #8899a6;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button:hover {
  color: #ffffff;
}

.modal-body {
  padding: 20px;
}

.ski-settings-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.setting-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #38444d;
}

.setting-item:last-child {
  border-bottom: none;
}

.setting-item label {
  font-weight: bold;
  color: #d9d9d9;
  font-size: 0.9em;
}

.setting-item span {
  color: #1da1f2;
  font-weight: 500;
}

.no-settings {
  text-align: center;
  padding: 20px 0;
}

.no-settings p {
  color: #8899a6;
  margin-bottom: 16px;
}

.update-settings-link {
  display: inline-block;
  background-color: #1da1f2;
  color: #ffffff;
  text-decoration: none;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: bold;
  font-size: 0.9em;
  transition: background-color 0.3s ease;
}

.update-settings-link:hover {
  background-color: #0c8ddb;
}

/* Mobile responsive */
@media screen and (max-width: 600px) {
  .modal-content {
    width: 95%;
    margin: 10px;
  }
  
  .modal-header,
  .modal-body {
    padding: 12px 16px;
  }
  
  .setting-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
}
</style>
